import React, { useState, useMemo, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { FiInfo, FiPlus } from 'react-icons/fi';
import AssessmentPopup from './AssessmentPopup';
import { useAssessmentContext } from '../AssessmentContext';
import { useTests } from '../../../hooks/useTests';
import TestsDataTable from './TestsDataTable';
import CreateTestPanel from './CreateTestPanel';
import BatchWizard from '../Wizard/BatchWizard';
import './assessment.css';
import '../assessment-module-mobile.css';

import { useNavigate } from 'react-router-dom';

const STEPS = [
  { id: 'create', label: 'Create Test', path: '/workflow/create' },
  { id: 'question-bank', label: 'Question Bank', path: '/workflow/question-bank' },
  { id: 'template', label: 'Template', path: '/workflow/template' },
  { id: 'groups', label: 'Groups & Assign', path: '/workflow/groups' },
  { id: 'publish', label: 'Save', path: '/workflow/publish' }
];

const loadDraft = () => {
  const draftStr = localStorage.getItem('draft_assessment');
  if (draftStr) {
    try {
      return JSON.parse(draftStr);
    } catch (e) {
      console.error('Failed to parse draft_assessment', e);
    }
  }
  return null;
};

const saveDraft = (a) => {
  localStorage.setItem('draft_assessment', JSON.stringify(a));
  window.dispatchEvent(new Event('draft-updated'));
};

const clearDraft = () => {
  localStorage.removeItem('draft_assessment');
  window.dispatchEvent(new Event('draft-updated'));
};

const generateRandomCode = () => `TST-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

const newAssessment = () => ({
  id: generateRandomCode(),
  name: '',
  category: 'Internal',
  type: '',
  instructions: '',
  videoEnabled: false,
  videoFiles: [],
  navigationControl: false,
  autoSubmit: false,
  fullscreenMode: false,
  tabSwitchWarning: false,
  status: 'Draft',
  currentStep: 'create',
  completedSteps: [],
  questionBank: null,
  template: null,
  groups: [],
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
});

const CATEGORY_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'internal', label: 'Internal', dbValue: 'Enova' },
  { value: 'external', label: 'External', dbValue: 'HR Recruitment-Fresher' },
];

const TestDashboard = () => {
  const navigate = useNavigate();
  const { setActiveModule, navigateToModule } = useAssessmentContext();
  const { tests, loading, refreshTests, updateTest, deleteTest, removeTestPack } = useTests();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef(null);
  const [popupConfig, setPopupConfig] = useState({ isOpen: false });

  const [localDraftsList, setLocalDraftsList] = useState([]);
  const [localPublished, setLocalPublished] = useState([]);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [draft, setDraft] = useState(null);

  const loadLocalTests = () => {
    const drafts = [];

    // 1. Load active draft
    const draftStr = localStorage.getItem('draft_assessment');
    if (draftStr) {
      try {
        const parsed = JSON.parse(draftStr);
        if (parsed && parsed.name) {
          drafts.push({
            ...parsed,
            id: parsed.id || 'draft-assessment',
            isActiveDraft: true,
            isLocalDraft: true,
            isLocal: true,
            status: 'Draft',
            created_at: parsed.createdAt || new Date().toISOString()
          });
        }
      } catch (e) {
        console.error(e);
      }
    }

    // 2. Load saved drafts
    const savedDraftsStr = localStorage.getItem('saved_drafts');
    if (savedDraftsStr) {
      try {
        const parsed = JSON.parse(savedDraftsStr);
        if (Array.isArray(parsed)) {
          parsed.forEach(d => {
            if (d && d.name) {
              drafts.push({
                ...d,
                isSavedDraft: true,
                isLocalDraft: true,
                isLocal: true,
                status: 'Draft',
                created_at: d.createdAt || new Date().toISOString()
              });
            }
          });
        }
      } catch (e) {
        console.error(e);
      }
    }

    setLocalDraftsList(drafts);

    // 3. Load published tests
    const pubStr = localStorage.getItem('published_tests');
    if (pubStr) {
      try {
        const parsed = JSON.parse(pubStr);
        if (Array.isArray(parsed)) {
          setLocalPublished(parsed.map(t => ({
            ...t,
            id: 'pub-' + t.id,
            isLocalPublished: true,
            isLocal: true,
            status: 'Published',
            created_at: t.createdAt || new Date().toISOString()
          })));
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      setLocalPublished([]);
    }
  };

  useEffect(() => {
    const sync = () => {
      setDraft(loadDraft());
      loadLocalTests();
    };
    sync();
    window.addEventListener("draft-updated", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("draft-updated", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  useEffect(() => {
    const showReminder = localStorage.getItem('show_publish_reminder_popup');
    if (showReminder === 'true') {
      localStorage.removeItem('show_publish_reminder_popup');
      setPopupConfig({
        isOpen: true,
        type: 'info',
        title: 'Publish Test',
        message: 'The test is saved, but you need to publish the test.',
        confirmText: 'OK',
        showCancel: false,
        onConfirm: () => {
          setPopupConfig({ isOpen: false });
        }
      });
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreateNew = () => {
    const existing = loadDraft();
    if (existing && existing.name) {
      setDraft(existing);
      setResumeOpen(true);
      return;
    }
    if (existing) clearDraft();
    const a = newAssessment();
    saveDraft(a);
    navigate("/workflow/create");
  };

  const resumeDraft = (testToResume) => {
    setResumeOpen(false);
    const targetDraft = (testToResume && typeof testToResume === 'object' && 'id' in testToResume) ? testToResume : draft;
    if (!targetDraft) return;

    const activeDraft = loadDraft();
    if (targetDraft.isSavedDraft) {
      const savedDraftsStr = localStorage.getItem('saved_drafts') || '[]';
      try {
        let savedDrafts = JSON.parse(savedDraftsStr);
        savedDrafts = savedDrafts.filter(d => d.id !== targetDraft.id);
        if (activeDraft && activeDraft.name && activeDraft.id !== targetDraft.id) {
          savedDrafts.unshift(activeDraft);
        }
        localStorage.setItem('saved_drafts', JSON.stringify(savedDrafts));
      } catch (e) {
        console.error(e);
      }
    }

    const cleanTest = { ...targetDraft };
    delete cleanTest.isSavedDraft;
    delete cleanTest.isLocalDraft;
    delete cleanTest.isLocal;
    delete cleanTest.status;
    delete cleanTest.created_at;
    delete cleanTest.isActiveDraft;

    saveDraft(cleanTest);
    const step = STEPS.find(s => s.id === cleanTest.currentStep) ?? STEPS[0];
    navigate(step.path);
  };

  const startFresh = () => {
    const existing = loadDraft();
    if (existing && existing.name) {
      const savedDraftsStr = localStorage.getItem('saved_drafts') || '[]';
      try {
        const savedDrafts = JSON.parse(savedDraftsStr);
        const filtered = savedDrafts.filter(d => d.id !== existing.id);
        filtered.unshift(existing);
        localStorage.setItem('saved_drafts', JSON.stringify(filtered));
      } catch (e) {
        console.error('Failed to save draft to saved_drafts', e);
      }
    }
    clearDraft();
    const a = newAssessment();
    saveDraft(a);
    setResumeOpen(false);
    navigate("/workflow/create");
  };

  const handleResumeDraftClick = (test) => {
    setDraft(test);
    setResumeOpen(true);
  };

  const handleEditTest = (test) => {
    if (test.isLocalDraft) {
      setDraft(test);
      resumeDraft(test);
    } else if (test.isLocalPublished) {
      const activeDraft = loadDraft();
      if (activeDraft && activeDraft.name) {
        const savedDraftsStr = localStorage.getItem('saved_drafts') || '[]';
        try {
          let savedDrafts = JSON.parse(savedDraftsStr);
          savedDrafts = savedDrafts.filter(d => d.id !== activeDraft.id);
          savedDrafts.unshift(activeDraft);
          localStorage.setItem('saved_drafts', JSON.stringify(savedDrafts));
        } catch (e) {
          console.error(e);
        }
      }
      const cleanTest = { ...test };
      delete cleanTest.isLocalPublished;
      delete cleanTest.isLocal;
      cleanTest.status = 'Draft';
      if (cleanTest.id && cleanTest.id.startsWith('pub-')) {
        cleanTest.id = cleanTest.id.substring(4);
      }
      
      // Prevent duplication: remove from published tests since it's now a draft again
      const pubStr = localStorage.getItem('published_tests');
      if (pubStr) {
        try {
          const parsed = JSON.parse(pubStr);
          const next = parsed.filter(t => String(t.id) !== String(cleanTest.id));
          localStorage.setItem('published_tests', JSON.stringify(next));
        } catch (e) {
          console.error(e);
        }
      }

      cleanTest.isEditMode = true;
      saveDraft(cleanTest);
      navigate(`/workflow/create`);
    } else {
      setEditingTest(test);
      setIsCreateOpen(true);
    }
  };

  const handleDeleteTest = (test) => {
    if (test.isLocalDraft) {
      setPopupConfig({
        isOpen: true,
        type: 'warning',
        title: 'Delete draft assessment?',
        message: 'Are you sure you want to delete this draft assessment? This action cannot be undone.',
        confirmText: 'Yes, delete it!',
        onConfirm: () => {
          if (test.isSavedDraft) {
            const savedDraftsStr = localStorage.getItem('saved_drafts') || '[]';
            try {
              const savedDrafts = JSON.parse(savedDraftsStr);
              const next = savedDrafts.filter(d => d.id !== test.id);
              localStorage.setItem('saved_drafts', JSON.stringify(next));
            } catch (e) {
              console.error(e);
            }
            window.dispatchEvent(new Event('draft-updated'));
          } else {
            clearDraft();
          }
          setPopupConfig({ isOpen: false });
          toast.success('Draft assessment deleted.');
          loadLocalTests();
        }
      });
    } else if (test.isLocalPublished) {
      setPopupConfig({
        isOpen: true,
        type: 'warning',
        title: 'Delete published assessment?',
        message: 'Are you sure you want to delete this published assessment? This action cannot be undone.',
        confirmText: 'Yes, delete it!',
        onConfirm: () => {
          const pubStr = localStorage.getItem('published_tests');
          if (pubStr) {
            try {
              const parsed = JSON.parse(pubStr);
              const next = parsed.filter(t => 'pub-' + t.id !== test.id);
              localStorage.setItem('published_tests', JSON.stringify(next));
              toast.success('Published assessment deleted.');
              loadLocalTests();
            } catch (e) {
              console.error(e);
            }
          }
          setPopupConfig({ isOpen: false });
          loadLocalTests();
        }
      });
    } else {
      setPopupConfig({
        isOpen: true,
        type: 'warning',
        title: 'Delete this test?',
        message: 'Are you sure you want to delete this test? This action cannot be undone.',
        confirmText: 'Yes, delete it!',
        onConfirm: async () => {
          try {
            await deleteTest(test.id);
            
            // Clean up from local published tests if it was synced
            const pubStr = localStorage.getItem('published_tests');
            if (pubStr) {
              try {
                const parsed = JSON.parse(pubStr);
                const next = parsed.filter(t => String(t.backendId) !== String(test.id));
                localStorage.setItem('published_tests', JSON.stringify(next));
              } catch (e) {
                console.error(e);
              }
            }

            setPopupConfig({ isOpen: false });
            toast.success('The assessment has been removed.');
            loadLocalTests();
            refreshTests();
          } catch (err) {
            console.error(err);
            setPopupConfig({ isOpen: false });
            toast.error('Failed to delete assessment');
          }
        }
      });
    }
  };

  const handleClosePanel = () => {
    setIsCreateOpen(false);
    setEditingTest(null);
  };

  const handleSuccessfulSave = () => {
    handleClosePanel();
    refreshTests();
  };

  const handleInitializeBatch = (assessment) => {
    setSelectedAssessment(assessment);
    setIsWizardOpen(true);
  };

  const activeCategoryFilter = CATEGORY_FILTER_OPTIONS.find((opt) => opt.value === categoryFilter);

  const allTestsCombined = useMemo(() => {
    const dbList = tests || [];
    const dbIds = new Set(dbList.map(t => String(t.id)));

    // Filter localPublished to exclude any that have already been synced (backendId matches a db ID)
    const filteredLocalPublished = (localPublished || []).filter(t => {
      if (t.backendId && dbIds.has(String(t.backendId))) {
        return false;
      }
      return true;
    });

    const list = dbList.map(dbTest => {
      const matchingLocal = (localPublished || []).find(t => String(t.backendId) === String(dbTest.id));
      if (matchingLocal && matchingLocal.groups) {
        const dbPackIds = new Set((dbTest.test_packs || []).map(p => String(p.id)));
        const localUnpublishedGroups = matchingLocal.groups.filter(g => !g.backendPackId && !dbPackIds.has(String(g.id)));
        return {
          ...dbTest,
          test_packs: [...(dbTest.test_packs || []), ...localUnpublishedGroups]
        };
      }
      return dbTest;
    });
    if (localDraftsList && localDraftsList.length > 0) {
      list.unshift(...localDraftsList);
    }
    if (filteredLocalPublished.length > 0) {
      list.unshift(...filteredLocalPublished);
    }
    return list;
  }, [tests, localDraftsList, localPublished]);

  const filteredTests = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return (allTestsCombined || []).filter((t) => {
      const name = t.name || '';
      const category = t.category || '';
      const categoryLabel =
        category === 'Enova'
          ? 'internal'
          : category === 'HR Recruitment-Fresher'
            ? 'external'
            : category.toLowerCase();
      const matchesSearch =
        !query ||
        name.toLowerCase().includes(query) ||
        category.toLowerCase().includes(query) ||
        categoryLabel.includes(query);
      const matchesCategory =
        !activeCategoryFilter?.dbValue || category === activeCategoryFilter.dbValue;
      return matchesSearch && matchesCategory;
    });
  }, [allTestsCombined, searchQuery, categoryFilter, activeCategoryFilter]);

  return (
    <div className="assess-mod-page assess-mod-inventory-page p-6 min-h-0 w-full">
      <div className="assess-mod-inventory-header assess-mod-header flex items-center justify-between mb-8">
        <div className="assess-mod-header-brand">
          <h3 className="text-2xl font-bold text-slate-800">Learning Management System</h3>
        </div>
        <div className="assess-mod-inventory-actions assess-mod-actions-row flex gap-3">
          <button 
            id="btn_new_test_toggle"
            className={`btn-assessment btn-assessment-danger ${isCreateOpen ? 'active' : ''}`}
            onClick={handleCreateNew}
          >
            <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] transition-colors ${isCreateOpen ? 'bg-red-800' : 'bg-white/20'}`}>
              1
            </span>
            <span>Create New Test</span>
          </button>
          <button 
            className="btn-assessment btn-assessment-danger"
            onClick={() => setActiveModule('templates')}
          >
            <span className="flex items-center justify-center w-5 h-5 bg-white/20 rounded-full text-[10px]">
              <FiPlus />
            </span>
            <span>Template Master</span>
          </button>
        </div>
      </div>

      <CreateTestPanel 
        isOpen={isCreateOpen}
        onClose={handleClosePanel}
        editingTest={editingTest}
        onSuccess={handleSuccessfulSave}
      />

      <BatchWizard 
        isOpen={isWizardOpen}
        onClose={() => setIsWizardOpen(false)}
        assessment={selectedAssessment}
      />

      <div className="assess-mod-inventory-card assess-mod-card bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-100/50 overflow-visible mb-12">
        <div className="assess-mod-inventory-toolbar assess-mod-tabs-row px-8 py-6 border-b border-slate-100 bg-slate-100 flex items-center justify-end">
          <div className="assess-mod-inventory-filters flex items-center gap-4">
            <div className="relative" ref={categoryDropdownRef}>
              <button 
                type="button"
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                className="flex items-center gap-3 bg-white h-10 px-4 rounded-xl border border-slate-100 shadow-sm hover:border-red-200 transition-all min-w-[160px]"
              >
                <i className="bi bi-filter text-slate-400 text-sm"></i>
                <span className="text-xs font-bold text-slate-600 uppercase tracking-wide flex-1 text-left">
                  {activeCategoryFilter?.label || 'All'}
                </span>
                <i className={`bi bi-chevron-down text-xs text-slate-400 transition-transform duration-300 ${isCategoryDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isCategoryDropdownOpen && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-100 rounded-2xl shadow-2xl z-[100] py-2 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300">
                  {CATEGORY_FILTER_OPTIONS.map((option) => (
                    <div 
                      key={option.value || 'all'}
                      onClick={() => {
                        setCategoryFilter(option.value);
                        setIsCategoryDropdownOpen(false);
                      }}
                      className={`px-4 py-2.5 text-xs font-bold uppercase tracking-wide cursor-pointer transition-all flex items-center gap-2 ${
                        categoryFilter === option.value 
                          ? 'bg-red-50 text-red-600' 
                          : 'text-slate-500 hover:bg-slate-50 hover:text-red-500'
                      }`}
                    >
                      <i className={`bi ${
                        option.value === 'internal' ? 'bi-building-fill' : 
                        option.value === 'external' ? 'bi-globe2' : 
                        'bi-grid-fill'
                      } text-[10px]`}></i>
                      <span>{option.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Search Input */}
            <div className="flex items-center gap-3 bg-white h-10 px-4 rounded-xl border border-slate-100 shadow-sm w-[320px] focus-within:border-red-200 transition-all">
              <i className="bi bi-search text-slate-400 text-sm"></i>
              <input 
                type="text" 
                className="text-xs font-bold text-slate-600 border-0 focus:ring-0 p-0 w-full placeholder:text-slate-400 uppercase tracking-wide outline-none bg-transparent"
                placeholder="Search by name or category..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Result Analysis Button */}
            <button
              id="btn_result_analysis"
              onClick={() => navigateToModule('analytics', 'inventory')}
              className="flex items-center gap-2 px-4 h-10 bg-[#B91C1C] text-white rounded-xl text-xs font-bold shadow-md hover:bg-[#991B1B] shadow-red-100/30 transition-all cursor-pointer border-0 outline-none"
            >
              <i className="bi bi-bar-chart-fill text-sm"></i>
              <span>Result Analysis</span>
            </button>
          </div>
        </div>
        <div className="assess-mod-inventory-table-wrap assess-mod-table-wrap p-0">
          {loading ? (
            <div className="py-32 flex flex-col items-center justify-center gap-6">
              <div className="w-14 h-14 border-[4px] border-slate-100 border-t-red-600 rounded-full animate-spin"></div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Synchronizing Inventory...</p>
            </div>
          ) : (
            <TestsDataTable 
              data={filteredTests}
              onEdit={handleEditTest}
              onDelete={handleDeleteTest}
              refreshData={refreshTests}
              onPackDeleted={removeTestPack}
              onResumeDraftClick={handleResumeDraftClick}
            />
          )}
        </div>
      </div>
      <AssessmentPopup 
        {...popupConfig}
        onCancel={popupConfig.onCancel || (() => setPopupConfig({ ...popupConfig, isOpen: false }))}
      />

      {resumeOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl border border-slate-100 animate-in fade-in zoom-in duration-300 relative p-6 text-center">
            {/* Close Button */}
            <button 
              type="button"
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors bg-transparent border-0 cursor-pointer p-0"
              onClick={() => setResumeOpen(false)}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Info Icon */}
            <div className="w-12 h-12 bg-rose-50 text-rose-500 rounded-2xl flex items-center justify-center text-xl mx-auto mb-4 border border-rose-100/50">
              <FiInfo />
            </div>

            <h3 className="text-lg font-black text-slate-800 tracking-tight mb-2">Resume Draft Assessment?</h3>
            <p className="text-xs text-slate-500 font-bold leading-relaxed mb-6">
              You have an unfinished assessment: <span className="text-rose-600 font-extrabold">{draft?.name}</span>. Would you like to resume where you left off, or start a new one?
            </p>

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={startFresh}
                className="btn-assessment btn-assessment-secondary flex items-center justify-center text-xs font-bold border border-slate-200 text-slate-600 hover:bg-slate-50 !bg-white !text-slate-700 hover:translate-y-[-1px] active:translate-y-0"
              >
                Create New Assessment
              </button>
              <button
                onClick={() => resumeDraft()}
                className="btn-assessment btn-assessment-danger flex items-center justify-center text-xs font-bold"
              >
                Resume Draft
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestDashboard;
